import { useEffect, useState } from "react";
import axios from "axios";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import Header from "@/components/Header";
import Footer from "@/components/Footer";
import { ChevronDown, Search, X } from "lucide-react";
import { useAuth } from "@/contexts/AuthContext";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/**
 * /faq — Public FAQ page.
 * Renders the owner-editable Q&A catalog grouped by category. Each question
 * is a click-to-expand accordion. Anchor links (#faq-<id>) deep-link individual
 * answers from anywhere on the site.
 *
 * 📜 Welcome to the Pyre-Lands — onboarding quest:
 *   Logged-in users see a sticky progress banner. Expanding 3 different
 *   FAQs awards 50 🪙 Chits (one-time). State + reward are server-side.
 */
export default function Faq() {
  const { isAuthed } = useAuth();
  const [groups, setGroups] = useState({});
  const [cats, setCats] = useState([]);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(() => new Set());
  const [query, setQuery] = useState("");
  const [quest, setQuest] = useState(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const { data } = await axios.get(`${API}/faqs`);
        if (cancelled) return;
        setGroups(data?.grouped || {});
        setCats(data?.categories || []);
      } catch {
        /* noop */
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  // Pull the Pyre-Lands quest state once authed
  useEffect(() => {
    if (!isAuthed) return;
    let cancelled = false;
    (async () => {
      try {
        const { data } = await axios.get(`${API}/quests/pyre-faq`);
        if (!cancelled) setQuest(data);
      } catch { /* noop */ }
    })();
    return () => { cancelled = true; };
  }, [isAuthed]);

  // Auto-open the hash-anchored FAQ on first paint
  useEffect(() => {
    if (loading) return;
    const hash = window.location.hash;
    if (hash?.startsWith("#faq-")) {
      const id = hash.replace("#faq-", "");
      setOpen((s) => new Set([...s, id]));
      const t = setTimeout(() => {
        const el = document.getElementById(`faq-${id}`);
        if (el) el.scrollIntoView({ behavior: "smooth", block: "center" });
      }, 200);
      return () => clearTimeout(t);
    }
  }, [loading]);

  function toggle(id) {
    setOpen((s) => {
      const n = new Set(s);
      if (n.has(id)) n.delete(id);
      else n.add(id);
      return n;
    });

    // 📜 Pyre-Lands quest: fire the read endpoint when a logged-in user
    // expands a fresh FAQ. Server enforces all the "3 unique reads" logic
    // and idempotency. Fire-and-forget — never block the UI.
    if (isAuthed && quest && !quest.claimed) {
      (async () => {
        try {
          const { data } = await axios.post(`${API}/quests/pyre-faq/read`, { faq_id: id });
          setQuest(data);
          if (data?.granted) {
            toast.success(`📜 Welcome to the Pyre-Lands! +50 🪙 Chits awarded.`, { duration: 6000 });
          }
        } catch {
          /* noop — quest tracking is best-effort */
        }
      })();
    }
  }

  // Search filter
  const q = query.trim().toLowerCase();
  const filtered = q
    ? cats
        .map((c) => [
          c,
          (groups[c] || []).filter(
            (f) =>
              f.question.toLowerCase().includes(q) ||
              f.answer.toLowerCase().includes(q)
          ),
        ])
        .filter(([, list]) => list.length > 0)
    : cats.map((c) => [c, groups[c] || []]);

  return (
    <div className="min-h-screen bg-black text-white" data-testid="faq-page">
      <Header />

      <main className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 pt-28 pb-20">
        <div className="text-center mb-10">
          <div className="text-[10px] uppercase tracking-[0.4em] text-[#D4AF37] font-bold">
            Help Center
          </div>
          <h1 className="font-display text-4xl sm:text-5xl lg:text-6xl leading-none mt-2">
            Frequently Asked
          </h1>
          <p className="text-neutral-400 text-sm mt-3 max-w-prose mx-auto">
            Everything about OmniMuscle's mechanics — eggs, tokens, streaks, premium, the chef, and more.
            Still stuck? <Link to="/#contact" className="text-[#D4AF37] hover:underline">Send us a message</Link>.
          </p>
        </div>

        {/* 🔎 Sticky search — 147 entries means findability matters. Sticky
            below the header so the search bar stays visible while scrolling. */}
        <div className="sticky top-16 z-20 -mx-4 sm:-mx-6 lg:-mx-8 px-4 sm:px-6 lg:px-8 py-3 bg-black/95 backdrop-blur-sm border-y border-zinc-900 mb-6">
          <div className="relative">
            <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-neutral-500 pointer-events-none" />
            <input
              data-testid="faq-search"
              type="text"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="Search 147 FAQs — try 'awakening', 'macros', or 'dungeon'…"
              className="input-omni w-full pl-10 pr-10"
              autoComplete="off"
            />
            {query && (
              <button
                type="button"
                onClick={() => setQuery("")}
                data-testid="faq-search-clear"
                className="absolute right-3 top-1/2 -translate-y-1/2 text-neutral-500 hover:text-white"
              >
                <X size={16} />
              </button>
            )}
          </div>
          {query.trim() && !loading && (
            <div className="mt-1.5 text-[10px] uppercase tracking-widest text-neutral-500" data-testid="faq-search-count">
              {filtered.reduce((sum, [, list]) => sum + list.length, 0)} match{filtered.reduce((sum, [, list]) => sum + list.length, 0) === 1 ? "" : "es"} across {filtered.length} categor{filtered.length === 1 ? "y" : "ies"}
            </div>
          )}
        </div>

        {isAuthed && quest && (
          <PyreLandsQuestBanner quest={quest} />
        )}

        {loading ? (
          <div className="text-center text-neutral-500 py-12">Loading…</div>
        ) : filtered.length === 0 ? (
          <div className="text-center text-neutral-500 py-12 border border-zinc-900 bg-[#0F0F0F]" data-testid="faq-empty">
            No FAQs match "{query}". Try a different keyword.
          </div>
        ) : (
          filtered.map(([category, items]) => (
            <section key={category} className="mb-10" data-testid={`faq-category-${category.replace(/\s+/g, '-').toLowerCase()}`}>
              <div className="text-[10px] uppercase tracking-[0.3em] text-[#D4AF37] font-bold mb-3">
                {category}
              </div>
              <div className="space-y-2">
                {items.map((f) => {
                  const isOpen = open.has(f.faq_id);
                  return (
                    <div
                      key={f.faq_id}
                      id={`faq-${f.faq_id}`}
                      className="bg-[#0F0F0F] border border-zinc-900"
                      data-testid={`faq-item-${f.faq_id}`}
                    >
                      <button
                        onClick={() => toggle(f.faq_id)}
                        data-testid={`faq-toggle-${f.faq_id}`}
                        className="w-full text-left px-4 py-3 flex items-start justify-between gap-3 hover:bg-zinc-900/40 transition-colors"
                      >
                        <span className="text-white font-bold leading-snug">{f.question}</span>
                        <ChevronDown
                          size={18}
                          className={`flex-shrink-0 mt-0.5 text-[#D4AF37] transition-transform ${isOpen ? "rotate-180" : ""}`}
                        />
                      </button>
                      {isOpen && (
                        <div
                          className="px-4 pb-4 text-sm text-neutral-300 leading-relaxed whitespace-pre-line border-t border-zinc-900 pt-3"
                          data-testid={`faq-answer-${f.faq_id}`}
                        >
                          {f.answer}
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            </section>
          ))
        )}
      </main>

      <Footer />
    </div>
  );
}


/**
 * 📜 "Welcome to the Pyre-Lands" — onboarding quest banner.
 * - In-progress: shows X/3 progress + pulsing dots.
 * - Completed:   shows the trophy state w/ Chits reward stamp.
 */
function PyreLandsQuestBanner({ quest }) {
  const total = quest?.required_reads || 3;
  const count = Math.min(quest?.read_count || 0, total);
  const reward = quest?.reward_chits || 50;
  const claimed = !!quest?.claimed;

  if (claimed) {
    return (
      <div
        data-testid="pyre-faq-quest-banner-complete"
        className="mb-6 rounded-xl border-2 border-emerald-500/60 bg-gradient-to-br from-emerald-950/40 via-black/60 to-amber-950/30 p-4 shadow-[0_0_24px_rgba(16,185,129,0.18)] relative overflow-hidden"
      >
        <div className="absolute -top-4 -right-4 text-[100px] opacity-10 select-none">📜</div>
        <div className="relative">
          <div className="text-[10px] uppercase tracking-[0.4em] text-emerald-300 font-black">
            Quest Complete · Welcome to the Pyre-Lands
          </div>
          <div className="text-lg font-black text-emerald-100 mt-1">
            ✓ You read your scrolls, hero. <span className="text-amber-300">+{reward} 🪙 Chits</span> awarded.
          </div>
        </div>
      </div>
    );
  }

  return (
    <div
      data-testid="pyre-faq-quest-banner"
      className="mb-6 rounded-xl border-2 border-amber-500/50 bg-gradient-to-br from-amber-950/30 via-black/60 to-purple-950/30 p-4 shadow-[0_0_24px_rgba(245,158,11,0.18)] relative overflow-hidden"
    >
      <div className="absolute -top-4 -right-4 text-[100px] opacity-10 select-none">📜</div>
      <div className="relative">
        <div className="text-[10px] uppercase tracking-[0.4em] text-amber-300 font-black">
          New Quest · {quest?.title || "Welcome to the Pyre-Lands"}
        </div>
        <div className="text-sm sm:text-base text-amber-100 font-bold mt-1">
          Read <b>{total}</b> FAQs to learn the Pyre-Lands. Reward: <span className="text-amber-300 font-black">+{reward} 🪙 Chits</span>.
        </div>
        {/* Progress dots */}
        <div className="mt-3 flex items-center gap-1.5" data-testid="pyre-faq-progress">
          {Array.from({ length: total }).map((_, i) => {
            const filled = i < count;
            return (
              <span
                key={i}
                data-testid={`pyre-faq-dot-${i}`}
                className={`h-3 w-3 rounded-full ${
                  filled
                    ? "bg-amber-400 shadow-[0_0_6px_rgba(245,158,11,0.7)]"
                    : "bg-neutral-700 border border-neutral-600 animate-pulse"
                }`}
              />
            );
          })}
          <span className="ml-2 text-[10px] uppercase tracking-wider text-neutral-400">
            {count} / {total}
          </span>
        </div>
      </div>
    </div>
  );
}

